import React, { useEffect } from 'react';

import NearMeIcon from '@mui/icons-material/NearMe';
import { Button, Grid, IconButton, Stack } from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import { useGeolocated } from 'react-geolocated';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { ControlledMap, ShowErrors } from 'common/components';
import ReactHookFormInput from 'common/components/form/ReactHookFormInput';
import {
    ItemGeocodeResult,
    ListGeocodeResult,
    WrapperGeocodeResult,
} from 'common/components/maps';
import { DEFAULT_LAT, DEFAULT_LNG } from 'common/constants';

import * as actions from '../actions';
import * as selectors from '../selectors';

const LocationForm = ({
    canUpdate,
    creditId,
    errors,
    fetchGeocodePoint,
    geocodePointData,
    geocodeResult,
    handleClose,
    handleGeocode,
    handleOnSave,
    initialValues,
    isFetchingGeocodePoint,
    isSaving,
    setEmptyGeocodeResult,
    setShowGeocodeResults,
    showGeocodeResults,
}) => {
    const { t } = useTranslation();
    const { coords, getPosition, isGeolocationEnabled } = useGeolocated({
        positionOptions: { enableHighAccuracy: false },
        userDecisionTimeout: 5000,
        suppressLocationOnMount: true,
    });

    const {
        control,
        handleSubmit,
        formState: { isDirty },
        setValue,
        watch,
    } = useForm({
        defaultValues: initialValues,
        mode: 'onChange',
    });

    const formValues = watch();
    const position = formValues.position || {};

    useEffect(() => {
        if (coords) updateLocation(coords);
    }, [coords]);

    useEffect(() => {
        if (!isNil(geocodePointData))
            setValue('description', geocodePointData, { shouldDirty: true });
    }, [geocodePointData]);

    useEffect(() => {
        if (isEmpty(position)) return;
        const { latitude, longitude } = position;
        setValue('latitude', latitude, { shouldDirty: true });
        setValue('longitude', longitude, { shouldDirty: true });
    }, [position]);

    useEffect(() => {
        if (
            formValues.latitude !== initialValues.latitude ||
            formValues.longitude !== initialValues.longitude
        )
            fetchGeocodePoint(formValues.latitude, formValues.longitude);
    }, [formValues.latitude, formValues.longitude]);

    const isDisabled = !canUpdate;

    const updateLocation = (position) => {
        if (isEmpty(position)) return;
        const { latitude, longitude } = position;
        setValue('position', { latitude, longitude }, { shouldDirty: true });
        setValue('latitude', latitude, { shouldDirty: true });
        setValue('longitude', longitude, { shouldDirty: true });
    };

    const handleClickSave = (values) =>
        handleOnSave({ ...values, creditId }, () => handleClose());

    return (
        <Stack component="form" onSubmit={handleSubmit(handleClickSave)}>
            {geocodeResult?.length > 0 && showGeocodeResults && (
                <WrapperGeocodeResult>
                    <ListGeocodeResult
                        style={{
                            transform: 'translate3d(0px, 38px, 0px)',
                        }}
                    >
                        {geocodeResult.map((result, i) => (
                            <ItemGeocodeResult
                                key={i}
                                onClick={() => {
                                    setValue(
                                        'description',
                                        result.formatted_address,
                                        { shouldDirty: true },
                                    );
                                    const { lat, lng } =
                                        result.geometry.location;
                                    updateLocation({
                                        latitude: lat,
                                        longitude: lng,
                                    });
                                }}
                            >
                                {result.formatted_address}
                            </ItemGeocodeResult>
                        ))}
                    </ListGeocodeResult>
                </WrapperGeocodeResult>
            )}

            <ReactHookFormInput
                control={control}
                disabled={isDisabled || isFetchingGeocodePoint}
                name="description"
                onChange={(e) => {
                    const value = e.target.value;
                    if (value.length >= 6) handleGeocode(value);
                    else setEmptyGeocodeResult();
                }}
                onBlur={() =>
                    setTimeout(() => setShowGeocodeResults(false), 150)
                }
                onFocus={() => setShowGeocodeResults(true)}
                placeholder={t('Location')}
            />

            <Grid container>
                <Grid size="grow">
                    <ReactHookFormInput
                        control={control}
                        disabled={isDisabled}
                        name="latitude"
                        placeholder={t('Latitude')}
                    />
                </Grid>

                <Grid size="grow">
                    <ReactHookFormInput
                        control={control}
                        disabled={isDisabled}
                        name="longitude"
                        placeholder={t('Longitude')}
                    />
                </Grid>

                <IconButton
                    disabled={!isGeolocationEnabled || isSaving}
                    onClick={() => {
                        if (!isNil(coords)) {
                            const { latitude, longitude } = coords;
                            updateLocation({ latitude, longitude });
                        } else getPosition();
                    }}
                >
                    <NearMeIcon fontSize="inherit" />
                </IconButton>
            </Grid>

            <ControlledMap
                center={{
                    lat: formValues.latitude || DEFAULT_LAT,
                    lng: formValues.longitude || DEFAULT_LNG,
                }}
                disableDefaultUI={isDisabled}
                heightMap="206px"
                onChange={(position) =>
                    setValue('position', position, {
                        shouldDirty: true,
                    })
                }
                readOnly={isDisabled}
                title={t('Location')}
                value={formValues.location}
            />

            <Grid container>
                <Grid
                    size={{ xs: 'grow' }}
                    sx={{
                        display: 'flex',
                        flexDirection: { md: 'row', xs: 'column' },
                        justifyContent: { md: 'right', xs: 'center' },
                    }}
                >
                    <Button
                        color="secondary"
                        onClick={handleClose}
                        sx={{
                            mr: { md: 2, xs: 0 },
                            order: { md: 1, xs: 2 },
                            width: { md: 'auto', xs: '100%' },
                        }}
                        variant="text"
                    >
                        {t('Cancel')}
                    </Button>

                    <Button
                        color="secondary"
                        disabled={!isDirty}
                        type="submit"
                        sx={{
                            mb: { md: 0, xs: 2 },
                            order: { md: 2, xs: 1 },
                            width: { md: 'auto', xs: '100%' },
                        }}
                        variant="outlined"
                    >
                        {isSaving ? t('Saving') : t('Save')}
                    </Button>
                </Grid>
            </Grid>

            <ShowErrors errors={errors} />
        </Stack>
    );
};

const mapStateToProps = createStructuredSelector({
    errors: selectors.getSaveLocationErrors,
    geocodePointData: selectors.getGeocodePointData,
    geocodeResult: selectors.getFetchGeocodeData,
    initialValues: selectors.getLocationInitialValues,
    isFetchingGeocodePoint: selectors.getIsFetchingGeocodePoint,
    isSaving: selectors.getIsSavingLocation,
    showGeocodeResults: selectors.showGeocodeResults,
});

const mapDispatchToProps = (dispatch) => ({
    fetchGeocodePoint: (lat, lng) =>
        dispatch(actions.fetchGeocodePoint(lat, lng)),
    handleGeocode: (str) => dispatch(actions.fetchGeocodeResult(str)),
    handleOnSave: (values, callback) =>
        dispatch(actions.saveLocation(values, callback)),
    setEmptyGeocodeResult: () => dispatch(actions.setEmptyGeocodeResult()),
    setShowGeocodeResults: (value) =>
        dispatch(actions.setShowGeocodeResults(value)),
});

LocationForm.propTypes = {
    canUpdate: PropTypes.bool,
    creditId: PropTypes.string,
    errors: PropTypes.array,
    fetchGeocodePoint: PropTypes.func,
    geocodePointData: PropTypes.object,
    geocodeResult: PropTypes.array,
    handleClose: PropTypes.func,
    handleGeocode: PropTypes.func,
    handleOnSave: PropTypes.func,
    initialValues: PropTypes.object,
    isFetchingGeocodePoint: PropTypes.bool,
    isSaving: PropTypes.bool,
    setEmptyGeocodeResult: PropTypes.func,
    setShowGeocodeResults: PropTypes.func,
    showGeocodeResults: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(LocationForm);
